import React, {FC, PropsWithChildren, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {ClickEventHandler, FrontClickEvent} from '../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DataEventActionsEnum} from '../../../libs/shared/core/src/models/tracking/dataEventActions';
import {DataEventObjectName} from '../../../libs/shared/core/src/models/tracking/dataEventObjectName';
import {useDataEventTracking} from '../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {AsyncActionButton} from '../../../libs/shared/ui/src/components/buttons/loadingButton/asyncActionButton';
import {InfoBubble, InfoBubbleProps} from '../../../libs/shared/ui/src/components/tip/infoBubble';
import {InfoBubbleContent} from '../../../libs/shared/ui/src/components/tip/infoBubbleContent';
import {InfoBubbleDescription} from '../../../libs/shared/ui/src/components/tip/infoBubbleContentDescription';
import {InfoBubbleTitle} from '../../../libs/shared/ui/src/components/tip/infoBubbleContentTitle';
import {InfoBubbleFooterActions} from '../../../libs/shared/ui/src/components/tip/infoBubbleFooterActions';
import {InfoBubbleHeroHeader} from '../../../libs/shared/ui/src/components/tip/infoBubbleHeroHeader';
import {
  InfoBubblePill,
  InfoBubblePillVariantsEnum,
} from '../../../libs/shared/ui/src/components/tip/infoBubblePill';
import {InfoBubbleStepCounter} from '../../../libs/shared/ui/src/components/tip/infoBubbleStepCounter';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {ExpandedDarkThemeProvider} from '../../../libs/shared/ui/src/theme/expandedDarkThemeProvider';

/*
 * Props.
 */

type BaseInfoBubbleProps = Pick<
  InfoBubbleProps,
  | 'anchor'
  | 'isVisible'
  | 'position'
  | 'isInteractive'
  | 'isStandalone'
  | 'overlayOffset'
  | 'overlayBorderRadius'
  | 'scrollsIntoView'
>;

interface EducationTipButtonProps {
  label: ReactNode;
  onClick: ClickEventHandler;
}

export interface EducationTipProps extends BaseInfoBubbleProps {
  hero?: ReactNode;
  title: ReactNode;
  pillType: InfoBubblePillVariantsEnum;
  onRequestClose: () => void;
  primaryActionLabel?: ReactNode;
  onPrimaryClick?: (event: FrontClickEvent) => Promise<void> | void;
  secondaryAction?: EducationTipButtonProps;
  stepCounter?: ReactNode;
  dataObjectName: DataEventObjectName;
}

/*
 * Styles.
 */

const StyledInfoBubble = styled(InfoBubble)`
  width: 380px;
`;

/*
 * Component.
 */

export const EducationTip: FC<PropsWithChildren<EducationTipProps>> = (props) => {
  const {
    anchor,
    children,
    position,
    isVisible,
    isInteractive,
    isStandalone,
    hero,
    title,
    pillType,
    onRequestClose,
    primaryActionLabel,
    onPrimaryClick,
    secondaryAction,
    stepCounter,
    dataObjectName,
  } = props;

  const {trackDataEvent} = useDataEventTracking();

  const [areActionButtonsDisabled, setAreActionButtonsDisabled] = useState(false);

  // Track close event only for education tips with no buttons
  const shouldTrackCloseEvent = !onPrimaryClick && !secondaryAction;
  useInitialEffect(() => {
    trackDataEvent(DataEventActionsEnum.VIEW, dataObjectName, {});
    return () => {
      if (shouldTrackCloseEvent) {
        trackDataEvent(DataEventActionsEnum.CLOSE, dataObjectName, {});
      }
    };
  });

  return (
    <ExpandedDarkThemeProvider>
      <StyledInfoBubble
        position={position}
        anchor={anchor}
        isVisible={isVisible}
        isInteractive={isInteractive}
        isStandalone={isStandalone}
        onRequestClose={onRequestClose}
        overlayOffset={props.overlayOffset}
        overlayBorderRadius={props.overlayBorderRadius}
        scrollsIntoView={props.scrollsIntoView}
        ignoreRepositionBoundary
      >
        {hero && <InfoBubbleHeroHeader>{hero}</InfoBubbleHeroHeader>}
        <InfoBubbleContent>
          <InfoBubblePill type={pillType} />
          <InfoBubbleTitle shouldWrapTitle>{title}</InfoBubbleTitle>
          {children && <InfoBubbleDescription>{children}</InfoBubbleDescription>}
          <InfoBubbleFooterActions $justifyContentRight>
            {stepCounter && <InfoBubbleStepCounter>{stepCounter}</InfoBubbleStepCounter>}

            {secondaryAction && (
              <Button
                onClick={(event) => {
                  setAreActionButtonsDisabled(true);
                  secondaryAction.onClick(event);
                }}
                size={VisualSizesEnum.MEDIUM}
                variant={ButtonVariantsEnum.LINK}
                isDisabled={areActionButtonsDisabled}
              >
                {secondaryAction.label}
              </Button>
            )}

            {primaryActionLabel && (
              <AsyncActionButton
                onClick={(event) => {
                  setAreActionButtonsDisabled(true);
                  onPrimaryClick?.(event);
                }}
                size={VisualSizesEnum.MEDIUM}
                variant={ButtonVariantsEnum.PRIMARY}
                isDisabled={areActionButtonsDisabled}
              >
                {primaryActionLabel}
              </AsyncActionButton>
            )}
          </InfoBubbleFooterActions>
        </InfoBubbleContent>
      </StyledInfoBubble>
    </ExpandedDarkThemeProvider>
  );
};
